import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type DragEvent,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';
import type {
  CharacterPortrait,
  PedaCategoryKey,
  ScreenPosition,
  SituationSortCategory,
  SituationSortSituation,
} from '../types';
import { DialogueBox } from './dialogue-box';
import { FormattedInlineText } from './formatted-inline-text';
import {
  categoryDisplayName,
  getSituationSortDisplayLabel,
  shuffleSituations,
  situationSortVerdict,
  stripLeadingPictographs,
} from '../utils/situation-sort-label';
import { normalizePedaCategory } from '../systems/peda-categories';
import { resolveCharacterPortrait, resolveMoodImage } from '../systems/character-runtime';
import { splitCharacterDisplayName } from '../systems/ally-trust';
import styles from './situation-sort-game.module.css';

const RECEIVE_FLASH_MS = 320;

type GamePhase = 'playing' | 'feedback' | 'summary';

interface FeedbackQueueItem {
  situationId: string;
  categoryId: string;
}

interface SituationSortGameProps {
  categories: SituationSortCategory[];
  situations: SituationSortSituation[];
  instructionText?: string;
  speakerName?: string;
  speakerCharacterId?: string;
  requireAllCorrect?: boolean;
  screenPosition?: ScreenPosition;
  characterData?: CharacterPortrait[];
  hasNextScene?: boolean;
  isShieldGainActive?: boolean;
  onShieldGain?: (payload: {
    amount: number;
    subtitle?: string;
    onDone: () => void;
  }) => void;
  onPedaAttempt?: (payload: { pedaCategory: string; correct: boolean }) => void;
  /** Retire les boucliers gagnés pendant la tentative en cours (retry après erreur). */
  onRetry?: (payload: { total: number; byCategory: Partial<Record<PedaCategoryKey, number>> }) => void;
  onHudProgressChange?: (label: string | null) => void;
  onComplete: (result: {
    correctCount: number;
    total: number;
    action: 'restart' | 'menu' | 'next';
  }) => void;
}

const SIGN_STROKE = {
  fill: 'none',
  stroke: '#dbb772',
  strokeWidth: 1.3,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

function CategorySign({ categoryId }: { categoryId: string }) {
  const id = String(categoryId);
  if (id === 'info_exposee') {
    return (
      <svg className={styles.binSign} viewBox="0 0 32 32" {...SIGN_STROKE} aria-hidden>
        <path d="M2 16s5-8 14-8 14 8 14 8-5 8-14 8S2 16 2 16Z" />
        <circle cx="16" cy="16" r="4" />
      </svg>
    );
  }
  if (id === 'intrusion' || id === 'A') {
    return (
      <svg className={styles.binSign} viewBox="0 0 32 32" {...SIGN_STROKE} aria-hidden>
        <rect x="6" y="14" width="20" height="14" rx="2.5" />
        <path d="M11 14v-4a5 5 0 0 1 9.5-2" />
        <circle cx="16" cy="21" r="1.6" />
      </svg>
    );
  }
  if (id === 'manipulation') {
    return (
      <svg className={styles.binSign} viewBox="0 0 32 32" {...SIGN_STROKE} aria-hidden>
        <path d="M4 12c0-3 2.5-5 5.5-5S15 9 15 12s-2.5 5-5.5 5S4 15 4 12Z" />
        <path d="M17 20c0-3 2.5-5 5.5-5S28 17 28 20s-2.5 5-5.5 5S17 23 17 20Z" />
        <path d="M13 15.5 19 17" />
      </svg>
    );
  }
  if (id === 'rien') {
    return (
      <svg
        className={styles.binSign}
        viewBox="0 0 32 32"
        fill="none"
        stroke="#7b8089"
        strokeWidth="1.3"
        strokeLinecap="round"
        aria-hidden
      >
        <circle cx="16" cy="16" r="11" />
        <path d="M11 16h10" />
      </svg>
    );
  }
  return (
    <svg className={styles.binSign} viewBox="0 0 32 32" {...SIGN_STROKE} aria-hidden>
      <circle cx="16" cy="16" r="11" />
    </svg>
  );
}

export const SituationSortGame = memo(({
  categories,
  situations,
  instructionText = 'Prenez la situation du dessus et posez-la dans la catégorie qui lui va.',
  speakerName = 'RSSI',
  speakerCharacterId = 'rssi',
  requireAllCorrect = false,
  characterData = [],
  hasNextScene,
  isShieldGainActive = false,
  onShieldGain,
  onPedaAttempt,
  onRetry,
  onHudProgressChange,
  onComplete,
}: SituationSortGameProps) => {
  const screenStyle: CSSProperties = {
    inset: 0,
    width: '100%',
    height: '100%',
  };

  const draggedSituationIdRef = useRef<string | null>(null);
  const flyHideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const receiveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cardRefs = useRef<Record<string, HTMLElement | null>>({});
  const shieldsEarnedThisAttemptRef = useRef<Partial<Record<PedaCategoryKey, number>>>({});
  const panelRef = useRef<HTMLDivElement>(null);
  const boardRef = useRef<HTMLDivElement>(null);
  const leadRef = useRef<HTMLParagraphElement>(null);
  const frozenBoardStyleRef = useRef<CSSProperties | null>(null);
  const frozenPanelOverflowRef = useRef(false);

  const [boardLayoutStyle, setBoardLayoutStyle] = useState<CSSProperties>({});
  const [panelOverflow, setPanelOverflow] = useState(false);

  const [phase, setPhase] = useState<GamePhase>('playing');
  const [orderedSituations, setOrderedSituations] = useState(() => shuffleSituations(situations));
  const [pileOrder, setPileOrder] = useState<string[]>(() => orderedSituations.map((s) => s.id));
  const [binOrder, setBinOrder] = useState<Record<string, string[]>>(() =>
    Object.fromEntries(categories.map((category) => [category.id, []]))
  );
  const [placements, setPlacements] = useState<Record<string, string | null>>(() =>
    Object.fromEntries(situations.map((s) => [s.id, null]))
  );
  const [heldSituationId, setHeldSituationId] = useState<string | null>(null);
  const [draggingSituationId, setDraggingSituationId] = useState<string | null>(null);
  const [dragOverTarget, setDragOverTarget] = useState<string | null>(null);
  const [receivedCategoryId, setReceivedCategoryId] = useState<string | null>(null);
  const [validateNote, setValidateNote] = useState<string | null>(null);
  const [feedbackIndex, setFeedbackIndex] = useState(0);
  const [feedbackQueue, setFeedbackQueue] = useState<FeedbackQueueItem[]>([]);
  const [results, setResults] = useState<boolean[]>([]);

  const situationById = useMemo(
    () => Object.fromEntries(orderedSituations.map((s) => [s.id, s])),
    [orderedSituations]
  );

  const unassignedSituations = useMemo(
    () => pileOrder.map((id) => situationById[id]).filter((s): s is SituationSortSituation => Boolean(s)),
    [pileOrder, situationById]
  );

  const visibleUnassigned = useMemo(
    () => unassignedSituations.filter((s) => s.id !== draggingSituationId),
    [unassignedSituations, draggingSituationId]
  );

  const topSituationId = visibleUnassigned[0]?.id ?? null;
  const allPlaced = unassignedSituations.length === 0;
  const isHolding = heldSituationId !== null;
  const placedCount = situations.length - unassignedSituations.length;
  const hasMoved = placedCount > 0;
  const pileFinished = unassignedSituations.length === 0;

  const speakerPortrait = speakerCharacterId
    ? resolveCharacterPortrait(characterData, speakerCharacterId)
    : undefined;
  const speakerSprite = speakerPortrait
    ? resolveMoodImage(speakerPortrait, 'happy') ?? speakerPortrait.defaultImage
    : undefined;
  const speakerDisplay = splitCharacterDisplayName(
    speakerPortrait?.name?.trim() || speakerName
  );

  const getSituationsInCategory = useCallback(
    (categoryId: string) =>
      (binOrder[categoryId] ?? [])
        .map((id) => situationById[id])
        .filter((s): s is SituationSortSituation => Boolean(s)),
    [binOrder, situationById]
  );

  const currentFeedback = feedbackQueue[feedbackIndex];
  const feedbackSituation = currentFeedback
    ? situations.find((s) => s.id === currentFeedback.situationId)
    : undefined;
  const feedbackCategoryId = currentFeedback?.categoryId ?? null;
  const isCorrectFeedback = feedbackSituation
    ? feedbackCategoryId === feedbackSituation.expectedCategoryId
    : false;
  const feedbackText = feedbackSituation
    ? (isCorrectFeedback ? feedbackSituation.feedbackOk : feedbackSituation.feedbackKo)
    : '';

  const getSituationQueueIndex = useCallback(
    (situationId: string) => feedbackQueue.findIndex((f) => f.situationId === situationId),
    [feedbackQueue]
  );

  const isSituationFeedbackRevealed = useCallback(
    (situationId: string) => {
      const qIdx = getSituationQueueIndex(situationId);
      if (qIdx < 0) return false;
      return qIdx <= feedbackIndex;
    },
    [getSituationQueueIndex, feedbackIndex]
  );

  const buildFeedbackQueue = useCallback(
    (currentBinOrder: Record<string, string[]>) => {
      const queue: FeedbackQueueItem[] = [];
      for (const category of categories) {
        for (const situationId of currentBinOrder[category.id] ?? []) {
          queue.push({ situationId, categoryId: category.id });
        }
      }
      return queue;
    },
    [categories]
  );

  const flashReceived = useCallback((categoryId: string) => {
    if (receiveTimerRef.current) clearTimeout(receiveTimerRef.current);
    setReceivedCategoryId(categoryId);
    receiveTimerRef.current = setTimeout(() => {
      setReceivedCategoryId(null);
      receiveTimerRef.current = null;
    }, RECEIVE_FLASH_MS);
  }, []);

  const clearHold = useCallback(() => {
    setHeldSituationId(null);
    draggedSituationIdRef.current = null;
  }, []);

  const assignSituation = useCallback((situationId: string, categoryId: string | null) => {
    setPlacements((prev) => ({
      ...prev,
      [situationId]: categoryId,
    }));
    setValidateNote(null);
  }, []);

  const placeInCategory = useCallback((situationId: string, categoryId: string) => {
    assignSituation(situationId, categoryId);
    setPileOrder((prev) => prev.filter((id) => id !== situationId));
    setBinOrder((prev) => {
      const next: Record<string, string[]> = {};
      for (const [id, cards] of Object.entries(prev)) {
        next[id] = cards.filter((cardId) => cardId !== situationId);
      }
      next[categoryId] = [...(next[categoryId] ?? []), situationId];
      return next;
    });
    flashReceived(categoryId);
    clearHold();
    setDraggingSituationId(null);
  }, [assignSituation, flashReceived, clearHold]);

  const returnToPile = useCallback((situationId: string) => {
    assignSituation(situationId, null);
    setPileOrder((prev) => (prev.includes(situationId) ? prev : [...prev, situationId]));
    setBinOrder((prev) => {
      const next: Record<string, string[]> = {};
      for (const [id, cards] of Object.entries(prev)) {
        next[id] = cards.filter((cardId) => cardId !== situationId);
      }
      return next;
    });
    setHeldSituationId((prev) => (prev === situationId ? null : prev));
    if (draggedSituationIdRef.current === situationId) {
      draggedSituationIdRef.current = null;
    }
    setDraggingSituationId((prev) => (prev === situationId ? null : prev));
  }, [assignSituation]);

  const toggleHoldTop = useCallback((situationId: string) => {
    if (situationId !== topSituationId) return;
    setHeldSituationId((prev) => (prev === situationId ? null : situationId));
  }, [topSituationId]);

  const moveSituationBy = useCallback((situationId: string, delta: number) => {
    const currentCategoryId = placements[situationId];
    if (!currentCategoryId) return;
    const currentIndex = categories.findIndex((c) => c.id === currentCategoryId);
    const target = categories[currentIndex + delta];
    if (currentIndex < 0 || !target) return;
    placeInCategory(situationId, target.id);
    requestAnimationFrame(() => {
      cardRefs.current[situationId]?.focus({ preventScroll: true });
    });
  }, [placements, categories, placeInCategory]);

  const handleDragStart = useCallback((e: DragEvent, situationId: string) => {
    if (phase !== 'playing') return;
    const isPlaced = Boolean(placements[situationId]);
    if (!isPlaced && situationId !== topSituationId) {
      e.preventDefault();
      return;
    }
    draggedSituationIdRef.current = situationId;
    setHeldSituationId(situationId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', situationId);
    if (flyHideTimerRef.current) clearTimeout(flyHideTimerRef.current);
    flyHideTimerRef.current = setTimeout(() => {
      setDraggingSituationId(situationId);
      flyHideTimerRef.current = null;
    }, 0);
  }, [phase, placements, topSituationId]);

  const handleDragEnd = useCallback(() => {
    if (flyHideTimerRef.current) {
      clearTimeout(flyHideTimerRef.current);
      flyHideTimerRef.current = null;
    }
    draggedSituationIdRef.current = null;
    setDraggingSituationId(null);
    setDragOverTarget(null);
    setHeldSituationId(null);
  }, []);

  const handleCategoryDragOver = useCallback((e: DragEvent, categoryId: string) => {
    if (phase !== 'playing') return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverTarget(categoryId);
  }, [phase]);

  const handleCategoryDrop = useCallback((e: DragEvent, categoryId: string) => {
    if (phase !== 'playing') return;
    e.preventDefault();
    setDragOverTarget(null);
    const situationId = e.dataTransfer.getData('text/plain') || draggedSituationIdRef.current;
    if (!situationId) return;
    placeInCategory(situationId, categoryId);
  }, [phase, placeInCategory]);

  const handlePileDragOver = useCallback((e: DragEvent) => {
    if (phase !== 'playing') return;
    const id = draggedSituationIdRef.current || heldSituationId;
    if (!id || !placements[id]) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverTarget('__pile__');
  }, [phase, heldSituationId, placements]);

  const handlePileDrop = useCallback((e: DragEvent) => {
    if (phase !== 'playing') return;
    const situationId = e.dataTransfer.getData('text/plain') || draggedSituationIdRef.current;
    if (!situationId || !placements[situationId]) return;
    e.preventDefault();
    setDragOverTarget(null);
    returnToPile(situationId);
  }, [phase, placements, returnToPile]);

  const handlePileClick = useCallback(() => {
    if (phase !== 'playing') return;
    if (heldSituationId && placements[heldSituationId]) {
      returnToPile(heldSituationId);
    }
  }, [phase, heldSituationId, placements, returnToPile]);

  const handleCardClick = useCallback((e: MouseEvent, situationId: string) => {
    if (phase !== 'playing') return;
    if (placements[situationId]) {
      returnToPile(situationId);
      return;
    }
    e.stopPropagation();
    toggleHoldTop(situationId);
  }, [phase, placements, returnToPile, toggleHoldTop]);

  const handleCardKeyDown = useCallback((e: KeyboardEvent<HTMLElement>, situationId: string) => {
    if (phase !== 'playing') return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (placements[situationId]) {
        returnToPile(situationId);
        return;
      }
      toggleHoldTop(situationId);
      return;
    }
    if (placements[situationId] && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
      e.preventDefault();
      e.stopPropagation();
      moveSituationBy(situationId, e.key === 'ArrowUp' ? -1 : 1);
    }
  }, [phase, placements, returnToPile, toggleHoldTop, moveSituationBy]);

  const handleBinClick = useCallback((categoryId: string) => {
    if (phase !== 'playing' || !heldSituationId) return;
    placeInCategory(heldSituationId, categoryId);
  }, [phase, heldSituationId, placeInCategory]);

  const handleBinKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>, categoryId: string) => {
    if (phase !== 'playing') return;
    if ((e.key === 'Enter' || e.key === ' ') && heldSituationId) {
      e.preventDefault();
      placeInCategory(heldSituationId, categoryId);
    }
  }, [phase, heldSituationId, placeInCategory]);

  const handleValidate = useCallback(() => {
    if (!allPlaced) {
      const left = unassignedSituations.length;
      setValidateNote(
        left > 1
          ? `Il reste ${left} situations à ranger.`
          : 'Il reste une situation à ranger.'
      );
      return;
    }
    const computed = situations.map((s) => placements[s.id] === s.expectedCategoryId);
    setResults(computed);
    setFeedbackQueue(buildFeedbackQueue(binOrder));
    setFeedbackIndex(0);
    setValidateNote(null);
    setHeldSituationId(null);
    setPhase('feedback');
  }, [allPlaced, unassignedSituations.length, situations, placements, binOrder, buildFeedbackQueue]);

  const goToSummary = useCallback(() => {
    setPhase('summary');
  }, []);

  const proceedAfterFeedbackDialogue = useCallback(() => {
    if (feedbackIndex < feedbackQueue.length - 1) {
      setFeedbackIndex((i) => i + 1);
      return;
    }
    goToSummary();
  }, [feedbackIndex, feedbackQueue.length, goToSummary]);

  const handleFeedbackContinue = useCallback(() => {
    if (phase !== 'feedback' || isShieldGainActive) return;

    const shieldsEarned = isCorrectFeedback ? (feedbackSituation?.pedaShields ?? 0) : 0;
    if (shieldsEarned > 0) {
      const category = normalizePedaCategory(feedbackSituation?.pedaCategory);
      if (category) {
        shieldsEarnedThisAttemptRef.current[category] = (
          shieldsEarnedThisAttemptRef.current[category] ?? 0
        ) + shieldsEarned;
      }
    }
    if (feedbackSituation?.pedaCategory && (feedbackSituation.pedaShields ?? 0) > 0) {
      onPedaAttempt?.({
        pedaCategory: feedbackSituation.pedaCategory,
        correct: isCorrectFeedback,
      });
    }
    if (shieldsEarned > 0 && onShieldGain) {
      onShieldGain({
        amount: shieldsEarned,
        subtitle: feedbackSituation?.pedaCategory,
        onDone: proceedAfterFeedbackDialogue,
      });
      return;
    }

    proceedAfterFeedbackDialogue();
  }, [
    phase,
    isShieldGainActive,
    isCorrectFeedback,
    feedbackSituation,
    onShieldGain,
    onPedaAttempt,
    proceedAfterFeedbackDialogue,
  ]);

  const resetGame = useCallback(() => {
    const nextOrder = shuffleSituations(situations);
    setPhase('playing');
    setOrderedSituations(nextOrder);
    setPileOrder(nextOrder.map((s) => s.id));
    setBinOrder(Object.fromEntries(categories.map((category) => [category.id, []])));
    setPlacements(Object.fromEntries(situations.map((s) => [s.id, null])));
    setFeedbackIndex(0);
    setFeedbackQueue([]);
    setResults([]);
    setHeldSituationId(null);
    setDraggingSituationId(null);
    setDragOverTarget(null);
    setReceivedCategoryId(null);
    setValidateNote(null);
    shieldsEarnedThisAttemptRef.current = {};
  }, [situations, categories]);

  const handleSummaryContinue = useCallback(() => {
    const allCorrect = results.length > 0 && results.every(Boolean);
    if (requireAllCorrect && !allCorrect) {
      const byCategory = shieldsEarnedThisAttemptRef.current;
      const total = Object.values(byCategory).reduce((sum, amount) => sum + (amount ?? 0), 0);
      if (total > 0) {
        onRetry?.({ total, byCategory });
      }
      resetGame();
      return;
    }
    onComplete({ correctCount: results.filter(Boolean).length, total: situations.length, action: 'next' });
  }, [requireAllCorrect, onComplete, onRetry, results, situations.length, resetGame]);

  const caleTri = useCallback(() => {
    const panel = panelRef.current;
    const board = boardRef.current;
    if (!panel || !board) return;

    if (panel.clientWidth < 320 || panel.clientHeight < 220) {
      setBoardLayoutStyle({});
      setPanelOverflow(false);
      frozenBoardStyleRef.current = null;
      frozenPanelOverflowRef.current = false;
      return;
    }

    if (phase === 'feedback' && frozenBoardStyleRef.current) {
      setBoardLayoutStyle(frozenBoardStyleRef.current);
      setPanelOverflow(frozenPanelOverflowRef.current);
      return;
    }

    const lame = leadRef.current;
    const badge = lame?.querySelector(`.${styles.nameplate}`);
    const badgeHeight = badge instanceof HTMLElement ? badge.offsetHeight : 0;
    const reserve = lame
      ? Math.min(
          Math.round(panel.clientHeight * 0.45),
          Math.round(panel.clientHeight * 0.11) + lame.offsetHeight + (badgeHeight > 0 ? badgeHeight + 9 : 0) + 6
        )
      : 0;

    board.style.transform = '';
    const w = panel.clientWidth - 36;
    const hh = panel.clientHeight - 28 - reserve;
    if (!board.offsetWidth || !board.offsetHeight || hh < 80) return;

    const brut = Math.min(w / board.offsetWidth, hh / board.offsetHeight, 1.5);
    const k = Math.max(1, brut);
    const overflow = brut < 1;
    const transform = Math.abs(k - 1) < 0.02 ? undefined : `scale(${k.toFixed(3)})`;

    const nextStyle: CSSProperties = {
      marginBottom: reserve > 0 ? `${reserve}px` : undefined,
      transform,
      transformOrigin: 'center center',
    };

    frozenBoardStyleRef.current = nextStyle;
    frozenPanelOverflowRef.current = overflow;
    setBoardLayoutStyle(nextStyle);
    setPanelOverflow(overflow);
  }, [phase]);

  useLayoutEffect(() => {
    if (phase === 'summary') return undefined;

    caleTri();

    const panel = panelRef.current;
    if (!panel) return undefined;

    const observer = new ResizeObserver(() => {
      caleTri();
    });
    observer.observe(panel);
    return () => observer.disconnect();
  }, [
    caleTri,
    phase,
    categories.length,
    situations.length,
    placedCount,
    pileFinished,
    instructionText,
    speakerDisplay.givenName,
    speakerDisplay.role,
  ]);

  useEffect(() => {
    if (phase === 'playing') {
      frozenBoardStyleRef.current = null;
      frozenPanelOverflowRef.current = false;
    }
  }, [phase]);

  useEffect(() => {
    if (!onHudProgressChange) return undefined;
    if (phase === 'summary') {
      onHudProgressChange(null);
      return () => onHudProgressChange(null);
    }
    if (phase === 'feedback') {
      onHudProgressChange(`Retour ${feedbackIndex + 1} / ${feedbackQueue.length}`);
      return () => onHudProgressChange(null);
    }
    onHudProgressChange(
      `${placedCount} / ${situations.length} classée${situations.length > 1 ? 's' : ''}`
    );
    return () => onHudProgressChange(null);
  }, [phase, placedCount, situations.length, feedbackIndex, feedbackQueue.length, onHudProgressChange]);

  useEffect(() => () => {
    if (flyHideTimerRef.current) clearTimeout(flyHideTimerRef.current);
    if (receiveTimerRef.current) clearTimeout(receiveTimerRef.current);
  }, []);

  const remainingLabel = unassignedSituations.length === 0
    ? 'PILE VIDE'
    : unassignedSituations.length > 1
      ? `RESTE ${unassignedSituations.length} SITUATIONS`
      : 'RESTE UNE SITUATION';

  const pileClass = [
    styles.pile,
    visibleUnassigned.length > 1 ? styles.pileHasOne : '',
    visibleUnassigned.length > 2 ? styles.pileHasTwo : '',
    unassignedSituations.length === 0 ? styles.pileEmpty : '',
    dragOverTarget === '__pile__' ? styles.pileDropTarget : '',
  ].filter(Boolean).join(' ');

  const getCategoryLabel = useCallback(
    (categoryId: string) => categories.find((c) => c.id === categoryId)?.label ?? categoryId,
    [categories]
  );

  const renderSituationCard = (
    situation: SituationSortSituation,
    options: { inPile?: boolean; isTop?: boolean }
  ) => {
    const idx = orderedSituations.findIndex((s) => s.id === situation.id);
    const inPile = options.inPile ?? false;
    const isTop = options.isTop ?? false;
    const isPlaced = Boolean(placements[situation.id]);
    const isFlying = draggingSituationId === situation.id;
    const revealed = phase !== 'playing' && isSituationFeedbackRevealed(situation.id);
    const correct = placements[situation.id] === situation.expectedCategoryId;
    const canDrag = phase === 'playing' && (isPlaced || isTop);

    const className = [
      styles.card,
      inPile && isTop ? styles.cardTop : '',
      heldSituationId === situation.id && !isFlying ? styles.cardHeld : '',
      isFlying ? styles.cardFlying : '',
      revealed && correct ? styles.cardOk : '',
      revealed && !correct ? styles.cardKo : '',
      phase !== 'playing' && isPlaced ? styles.cardJudged : '',
    ].filter(Boolean).join(' ');

    return (
      <article
        key={situation.id}
        ref={(el) => { cardRefs.current[situation.id] = el; }}
        className={className}
        draggable={canDrag}
        tabIndex={phase === 'playing' && (isPlaced || isTop) ? 0 : -1}
        onDragStart={(e) => handleDragStart(e, situation.id)}
        onDragEnd={handleDragEnd}
        onClick={(e) => handleCardClick(e, situation.id)}
        onKeyDown={(e) => handleCardKeyDown(e, situation.id)}
      >
        <span className={styles.cardTitle}>
          {getSituationSortDisplayLabel(situation, idx)}
        </span>
        <p className={styles.cardText}>
          <FormattedInlineText text={situation.text} />
        </p>
        {revealed && isPlaced && (
          <span className={styles.cardVerdict}>
            {situationSortVerdict(correct, getCategoryLabel(situation.expectedCategoryId))}
          </span>
        )}
        {isPlaced && phase === 'playing' && (
          <span className={styles.cardArrows}>
            <button
              type="button"
              title="Monter d'une catégorie"
              onClick={(e) => {
                e.stopPropagation();
                moveSituationBy(situation.id, -1);
              }}
            >
              ▲
            </button>
            <button
              type="button"
              title="Descendre d'une catégorie"
              onClick={(e) => {
                e.stopPropagation();
                moveSituationBy(situation.id, 1);
              }}
            >
              ▼
            </button>
          </span>
        )}
      </article>
    );
  };

  if (phase === 'summary') {
    const finalCorrect = results.filter(Boolean).length;
    const correctSituations = situations.filter(
      (s) => placements[s.id] === s.expectedCategoryId
    );
    const incorrectSituations = situations
      .filter((s) => placements[s.id] !== s.expectedCategoryId)
      .map((s) => ({
        situation: s,
        placedCategoryId: placements[s.id] as string,
      }));
    const allCorrect = finalCorrect === situations.length;
    const mustRetry = requireAllCorrect && !allCorrect;

    return (
      <div className={`${styles.overlay} ${styles.overlayFullscreen}`}>
        <div className={styles.screenArea} style={screenStyle}>
          <div className={`${styles.screenContent} ${styles.screenContentSummary}`}>
            <div className={`${styles.summaryCard} cn-zoom-ui`}>
              <div className={styles.summaryHero}>
                <div
                  className={styles.summaryScoreRing}
                  style={{ '--score-ratio': `${(finalCorrect / situations.length) * 100}%` } as CSSProperties}
                >
                  <div className={styles.summaryScoreInner}>
                    <span className={styles.summaryScoreValue}>{finalCorrect}</span>
                    <span className={styles.summaryScoreTotal}>/{situations.length}</span>
                  </div>
                </div>
                <p className={styles.summaryHeroTitle}>Bilan du classement</p>
                <p className={styles.summaryHeroSubtitle}>
                  {finalCorrect === situations.length
                    ? 'Toutes les situations sont bien catégorisées.'
                    : `${finalCorrect} situation${finalCorrect > 1 ? 's' : ''} correctement identifiée${finalCorrect > 1 ? 's' : ''}.`}
                </p>
              </div>

              <div className={styles.summaryRecap}>
                {correctSituations.length > 0 && (
                  <section className={styles.summarySection}>
                    <div className={`${styles.summarySectionHead} ${styles.summarySectionHeadCorrect}`}>
                      <span className={styles.summarySectionIcon} aria-hidden>✓</span>
                      <h3 className={styles.summarySectionTitle}>Bien classées</h3>
                      <span className={styles.summarySectionCount}>{correctSituations.length}</span>
                    </div>
                    <ul className={styles.summaryList}>
                      {correctSituations.map((situation) => {
                        const idx = orderedSituations.findIndex((s) => s.id === situation.id);
                        return (
                          <li key={situation.id} className={`${styles.summaryItem} ${styles.summaryItemCorrect}`}>
                            <div className={styles.summaryItemTop}>
                              <span className={styles.summaryItemIndex}>
                                {getSituationSortDisplayLabel(situation, idx)}
                              </span>
                              <span className={`${styles.summaryCategoryChip} ${styles.summaryCategoryChipCorrect}`}>
                                {categoryDisplayName(getCategoryLabel(placements[situation.id]!))}
                              </span>
                            </div>
                            <p className={styles.summaryItemText}>{situation.text}</p>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                )}

                {incorrectSituations.length > 0 && (
                  <section className={styles.summarySection}>
                    <div className={`${styles.summarySectionHead} ${styles.summarySectionHeadWrong}`}>
                      <span className={styles.summarySectionIcon} aria-hidden>✕</span>
                      <h3 className={styles.summarySectionTitle}>À revoir</h3>
                      <span className={styles.summarySectionCount}>{incorrectSituations.length}</span>
                    </div>
                    <ul className={styles.summaryList}>
                      {incorrectSituations.map(({ situation, placedCategoryId }) => {
                        const idx = orderedSituations.findIndex((s) => s.id === situation.id);
                        return (
                          <li key={situation.id} className={`${styles.summaryItem} ${styles.summaryItemWrong}`}>
                            <div className={styles.summaryItemTop}>
                              <span className={styles.summaryItemIndex}>
                                {getSituationSortDisplayLabel(situation, idx)}
                              </span>
                              <span className={`${styles.summaryCategoryChip} ${styles.summaryCategoryChipWrong}`}>
                                {categoryDisplayName(getCategoryLabel(placedCategoryId))}
                              </span>
                            </div>
                            <p className={styles.summaryItemText}>{situation.text}</p>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                )}
              </div>

              <button type="button" className={styles.summaryBtn} onClick={handleSummaryContinue}>
                {mustRetry ? 'Réessayer' : (hasNextScene ? 'Continuer' : 'Terminer')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const loupeVerdict = feedbackSituation
    ? situationSortVerdict(
        isCorrectFeedback,
        getCategoryLabel(feedbackSituation.expectedCategoryId)
      )
    : '';

  const boardClass = [
    styles.board,
    isHolding && phase === 'playing' ? styles.holding : '',
    phase === 'feedback' ? styles.judging : '',
    hasMoved ? styles.hasMoved : '',
    pileFinished ? styles.pileFinie : '',
  ].filter(Boolean).join(' ');

  const panelClass = [
    styles.screenContent,
    panelOverflow ? styles.screenContentOverflow : '',
  ].filter(Boolean).join(' ');

  const leadClass = [
    styles.lead,
    phase === 'feedback' ? styles.leadHidden : '',
  ].filter(Boolean).join(' ');

  // cn-taille-base : le jeu de tri garde ses tailles actuelles quel que soit le palier d'écran.
  return (
    <div className={`${styles.overlay} ${styles.overlayFullscreen} cn-taille-base`}>
      <div className={styles.screenArea} style={screenStyle}>
        <div className={panelClass} ref={panelRef}>
          {speakerSprite && (
            <div className={styles.character}>
              <img
                className={styles.characterSprite}
                src={speakerSprite}
                alt=""
              />
            </div>
          )}

          <div
            className={boardClass}
            ref={boardRef}
            style={boardLayoutStyle}
          >
            <div className={styles.header}>
              <div className={styles.title}>Signalement — tri des situations</div>
            </div>

            <div className={styles.cols}>
              <div className={styles.col}>
                <span className={`${styles.colLbl} ${styles.colLblPile}`}>La pile</span>
                <div
                  className={pileClass}
                  onDragOver={handlePileDragOver}
                  onDragLeave={() => setDragOverTarget(null)}
                  onDrop={handlePileDrop}
                  onClick={handlePileClick}
                >
                  <i className={styles.pileShadowDeep} aria-hidden />
                  <i className={styles.pileShadow} aria-hidden />
                  {unassignedSituations.map((situation) =>
                    renderSituationCard(situation, {
                      inPile: true,
                      isTop: situation.id === topSituationId,
                    })
                  )}
                </div>
                <div className={styles.remaining}>{remainingLabel}</div>

                {phase === 'feedback' && feedbackSituation && (
                  <div
                    className={[
                      styles.loupe,
                      isCorrectFeedback ? styles.loupeOk : styles.loupeKo,
                    ].join(' ')}
                  >
                    <span className={styles.loupeTitle}>
                      {getSituationSortDisplayLabel(
                        feedbackSituation,
                        orderedSituations.findIndex((s) => s.id === feedbackSituation.id)
                      )}
                    </span>
                    <p className={styles.loupeText}>
                      <FormattedInlineText text={feedbackSituation.text} />
                    </p>
                    <span className={styles.loupeVerdict}>{loupeVerdict}</span>
                  </div>
                )}
              </div>

              <div className={styles.col}>
                <span className={styles.colLbl}>Catégories</span>
                <div className={styles.bins}>
                  {categories.map((category) => {
                    const inZone = getSituationsInCategory(category.id);
                    const isDropTarget = dragOverTarget === category.id;
                    const isFeedbackBin = phase === 'feedback' && feedbackCategoryId === category.id;
                    return (
                      <div
                        key={category.id}
                        className={[
                          styles.bin,
                          isDropTarget ? styles.binDropTarget : '',
                          receivedCategoryId === category.id ? styles.binReceived : '',
                          isFeedbackBin && isCorrectFeedback ? styles.binOk : '',
                          isFeedbackBin && !isCorrectFeedback ? styles.binKo : '',
                        ].filter(Boolean).join(' ')}
                        tabIndex={phase === 'playing' ? 0 : -1}
                        onDragOver={(e) => handleCategoryDragOver(e, category.id)}
                        onDragLeave={() => setDragOverTarget(null)}
                        onDrop={(e) => handleCategoryDrop(e, category.id)}
                        onClick={() => handleBinClick(category.id)}
                        onKeyDown={(e) => handleBinKeyDown(e, category.id)}
                      >
                        <span className={styles.binLabel}>
                          <CategorySign categoryId={category.id} />
                          <span>
                            <FormattedInlineText text={stripLeadingPictographs(category.label)} />
                          </span>
                        </span>
                        <div
                          className={[
                            styles.binZone,
                            inZone.length > 0 ? styles.binZoneFilled : '',
                          ].filter(Boolean).join(' ')}
                        >
                          <span className={styles.binEmpty}>Poser ici</span>
                          {inZone.map((situation) =>
                            renderSituationCard(situation, { inPile: false })
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {phase === 'playing' && (
              <div className={styles.footer}>
                <span className={`${styles.note} ${validateNote ? styles.noteKo : ''}`}>
                  {validateNote ?? ''}
                </span>
                <button
                  type="button"
                  className={`${styles.validateBtn} ${allPlaced ? styles.validateReady : ''}`}
                  onClick={handleValidate}
                >
                  Valider le classement
                </button>
              </div>
            )}
          </div>

          <p className={leadClass} ref={leadRef}>
            {speakerDisplay.givenName && (
              <span className={styles.nameplate}>
                <span className={styles.nameChip}>
                  <b className={styles.nameGiven}>{speakerDisplay.givenName}</b>
                  {speakerDisplay.role && (
                    <span className={styles.nameRole}>{speakerDisplay.role}</span>
                  )}
                </span>
                <i className={styles.nameAtt}>attend votre signalement</i>
              </span>
            )}
            <FormattedInlineText text={instructionText} />
            <i className={styles.leadHint}>Une seule à la fois.</i>
          </p>
        </div>
      </div>

      {phase === 'feedback' && feedbackSituation && (
        <div className={styles.feedbackDialogueLayer}>
          <DialogueBox
            key={feedbackSituation.id}
            speaker={speakerName}
            text={feedbackText}
            onClick={handleFeedbackContinue}
            showContinue
            characterId={speakerCharacterId}
            characterData={characterData}
            className={isCorrectFeedback ? styles.feedbackVerdictOk : styles.feedbackVerdictKo}
          />
        </div>
      )}
    </div>
  );
});

SituationSortGame.displayName = 'SituationSortGame';
